import React from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "./ui/sidebar";
import { UpdateDialog } from "./update-dialog";
import { startNavigationFeedback } from "@/lib/navigation-feedback";

type NavPrimaryProps = {
  items: {
    title: string;
    url: string;
    icon?: any;
    className?: string;
  }[];
  showUpdateDialog?: boolean;
  onItemClick?: (item: { title: string; url: string }) => boolean | void;
} & React.ComponentProps<typeof SidebarGroup>;

export function NavPrimary({
  items,
  showUpdateDialog = false,
  onItemClick,
  ...props
}: NavPrimaryProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { setOpenMobile, isMobile } = useSidebar();

  // Create a modified items array to render with the update dialog inserted at the right position
  const itemsToRender = [...items];

  const handleItemClick = (item: { title: string; url: string }) => {
    // If onItemClick returns true, it means the click was handled externally
    if (onItemClick?.(item)) {
      return;
    }

    if (isMobile && item.title !== "Settings") setOpenMobile(false);
    if (item.url.includes("http")) {
      window.open(item.url, "_blank");
    } else {
      startNavigationFeedback();
      router.push(item.url);
    }
  };

  return (
    <SidebarGroup {...props}>
      <SidebarGroupContent>
        <SidebarMenu className="items-center">
          {itemsToRender.map((item, index) => (
            <React.Fragment key={item.title}>
              {/* Insert UpdateDialog before "Settings" */}
              {showUpdateDialog && index === items.length - 1 && (
                <SidebarMenuItem>
                  <UpdateDialog />
                </SidebarMenuItem>
              )}
              {/* Default */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  variant="custom"
                  tooltip={{
                    children: item.title,
                    hidden: false,
                  }}
                  onClick={() => handleItemClick(item)}
                  isActive={
                    pathname.includes(item.url) &&
                    item.title !== "Upgrade to Plus"
                  }
                  className={`flex size-8 !w-8 shrink-0 items-center !justify-center !gap-0 ${item.title === "Settings" && "settings-nav-btn"} ${item.title === "Upgrade to Plus" && "upgrade-nav-btn"}`}
                >
                  {item.icon && (
                    <item.icon className={`!size-5 ${item.className}`} />
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            </React.Fragment>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
